// MAMORU - 検知ロジック（クライアント側 第一段階）
// 拡張機能v0.2 のロジックをTypeScript化。文脈判定はサーバーAPIに後段委譲。

import type { DataBundle, DetectResult, Hit, Work } from './types';

const KANJI_RE = /^[一-鿿々〆〇]{2}$/;

// 旧字体・新字体の主要対応表（人名・キャラ名で頻出するもの）
const OLD_TO_NEW: Record<string, string> = {
  '禰': '祢', '煉': '錬', '靑': '青', '黑': '黒', '惡': '悪',
  '澤': '沢', '齋': '斎', '齊': '斉', '邊': '辺', '邉': '辺',
  '濱': '浜', '濵': '浜', '與': '与', '兒': '児', '櫻': '桜',
  '橫': '横', '德': '徳', '舊': '旧', '寶': '宝', '榮': '栄',
  '驛': '駅', '寢': '寝', '醫': '医', '萬': '万', '亂': '乱',
  '會': '会', '佛': '仏', '攜': '携', '單': '単', '國': '国',
  '臺': '台', '對': '対', '黨': '党', '當': '当', '盡': '尽',
  '聽': '聴', '讀': '読', '觀': '観', '證': '証', '辭': '辞',
  '處': '処', '雙': '双', '卽': '即', '來': '来', '價': '価',
  '兩': '両', '藏': '蔵', '蠶': '蚕', '巖': '巌', '榮': '栄',
  '冨': '富', '渕': '淵',
};

// 入力文字列を検索用に正規化:
// - NFKC（半角全角統一など）
// - IVS（異体字セレクタ U+E0100〜U+E01EF）除去
// - VS1〜VS16（U+FE00〜U+FE0F）除去
// - 旧字体 → 新字体に変換
export function normalizeText(s: string): string {
  if (!s) return s;
  let n = s.normalize('NFKC')
    .replace(/[\u{E0100}-\u{E01EF}]/gu, '')
    .replace(/[︀-️]/g, '');
  // 旧字体 → 新字体
  let out = '';
  for (const c of n) {
    out += OLD_TO_NEW[c] ?? c;
  }
  return out;
}

function charCategory(c: string): string {
  if (!c) return 'edge';
  if (/[ァ-ヴｦ-ﾟー・]/.test(c)) return 'kata';
  if (/[ぁ-んゔ]/.test(c)) return 'hira';
  if (/[一-鿿々〆〇]/.test(c)) return 'kanji';
  if (/[A-Za-zＡ-Ｚａ-ｚ]/.test(c)) return 'alpha';
  if (/[0-9０-９]/.test(c)) return 'digit';
  return 'sep';
}

function isAtWordBoundary(text: string, start: number, end: number): boolean {
  const matched = text.substring(start, end);
  const before = start > 0 ? text[start - 1] : '';
  const after = end < text.length ? text[end] : '';
  const fc = charCategory(matched[0]);
  const lc = charCategory(matched[matched.length - 1]);
  const bc = charCategory(before);
  const ac = charCategory(after);
  if (bc !== 'edge' && bc !== 'sep' && bc === fc) return false;
  if (ac !== 'edge' && ac !== 'sep' && ac === lc) return false;
  return true;
}

export class Detector {
  private data: DataBundle;
  private charNames: Map<string, number[]>;
  private aliasShort: Set<string>;
  private workNames: string[];
  private commonSet: Set<string>;

  constructor(data: DataBundle) {
    this.data = data;
    this.charNames = new Map();
    // DBのキャラ名も正規化キーで登録（旧字体・IVS含む登録名でも検索ヒットさせるため）
    for (const [name, ids] of Object.entries(data.db.chars)) {
      const norm = normalizeText(name);
      if (!this.charNames.has(norm)) this.charNames.set(norm, ids);
      // 元の表記もキーとして保持（DBに新字体が入っている場合に備える）
      if (norm !== name) this.charNames.set(name, ids);
    }

    // 愛称展開
    this.aliasShort = new Set();
    if (data.charAliases?.aliases) {
      for (const [canonical, list] of Object.entries(data.charAliases.aliases)) {
        const ids = this.charNames.get(normalizeText(canonical)) || this.charNames.get(canonical);
        if (!ids) continue;
        for (const alias of list) {
          if (alias.length < 2) continue;
          const a = normalizeText(alias);
          if (!this.charNames.has(a)) this.charNames.set(a, ids);
          if (a.length === 2) this.aliasShort.add(a);
        }
      }
    }

    // 作品名（位置取り用）
    const wn = new Set<string>();
    for (const w of Object.values(data.db.works)) {
      if (w?.t && w.t.length >= 3) wn.add(w.t);
    }
    if (data.workAliases?.aliases) {
      for (const arr of Object.values(data.workAliases.aliases)) {
        for (const a of arr) if (a && a.length >= 3) wn.add(a);
      }
    }
    this.workNames = Array.from(wn);

    this.commonSet = new Set(data.common.names);
  }

  private isAcceptableShort(name: string): boolean {
    if (name.length >= 3) return true;
    if (KANJI_RE.test(name)) return true;
    if (this.aliasShort.has(name)) return true;
    return false;
  }

  private workIsInText(work: Work, text: string): boolean {
    if (text.includes(work.t)) return true;
    const aliases = this.data.workAliases.aliases[work.t];
    if (aliases) {
      for (const a of aliases) if (text.includes(a)) return true;
    }
    return false;
  }

  detect(rawText: string, opts?: { mode?: 'A' | 'B' }): DetectResult {
    const mode = opts?.mode ?? 'A'; // Webアプリは入力＝チェック依頼確定なのでA相当
    if (!rawText || rawText.trim().length < 2) return { hits: [], level: 'none' };
    // 入力テキストも正規化して照合（旧字体「禰豆子」⇔ 新字体「祢豆子」など）
    const text = normalizeText(rawText);

    if (mode === 'B') {
      const ik = this.data.sites.intent_keywords;
      const lower = text.toLowerCase();
      const has = ik.ja.some((k) => text.includes(k))
                || ik.en.some((k) => lower.includes(k.toLowerCase()));
      if (!has) return { hits: [], level: 'none' };
    }

    const pdHit = this.data.pd.works.some((w) => text.includes(w));
    const found = this.findInText(text);
    const hits: Hit[] = [];

    for (const [name, ids] of found) {
      const works = ids.slice(0, 8).map((id) => this.data.db.works[String(id)]).filter(Boolean) as Work[];
      const matchedWork = works.find((w) => this.workIsInText(w, text));
      const isCommon = this.commonSet.has(name);

      let level: Hit['level'] | 'none' = 'none';
      if (matchedWork) {
        // 作品名共起がある場合のみ通常のレベル付け
        const matchedIsPD = this.data.pd.works.some(
          (p) => matchedWork.t.includes(p) || p.includes(matchedWork.t),
        );
        level = matchedIsPD ? 'green' : 'red';
      } else if (isCommon) {
        // 既知の一般語：作品名共起なしなら抑制
        level = 'none';
      } else if (pdHit) {
        level = 'green';
      } else if (name.length >= 3) {
        level = 'yellow';
      } else if (KANJI_RE.test(name) || this.aliasShort.has(name)) {
        level = 'yellow';
      }

      if (level === 'none') continue;
      hits.push({ name, level: level as Hit['level'], works: works.slice(0, 5), matchedWork });
    }

    const order = { red: 3, yellow: 2, green: 1 };
    hits.sort((a, b) => order[b.level] - order[a.level] || b.name.length - a.name.length);

    let topLevel: DetectResult['level'] = 'none';
    if (hits.some((h) => h.level === 'red')) topLevel = 'red';
    else if (hits.some((h) => h.level === 'yellow')) topLevel = 'yellow';
    else if (hits.some((h) => h.level === 'green')) topLevel = 'green';

    // キャラ未検出でも作品名が入力にあれば情報ヒット
    if (hits.length === 0 && !pdHit) {
      const wm = this.findWorkMentions(text);
      if (wm.length > 0) {
        return {
          hits: wm.slice(0, 5).map((w) => ({
            name: w.t,
            level: 'yellow' as const,
            works: [w],
            matchedWork: w,
          })),
          level: 'yellow',
          workOnly: true,
        };
      }
    }

    return { hits, level: topLevel };
  }

  // 入力テキスト内に存在する「作品名」をリスト化（重複除去 + エイリアス→正式名解決）
  private findWorkMentions(text: string): Work[] {
    const aliases = this.data.workAliases.aliases;
    const aliasToCanonical = new Map<string, string>();
    for (const [canonical, list] of Object.entries(aliases)) {
      for (const a of list) aliasToCanonical.set(a, canonical);
    }
    const seen = new Set<string>();
    const out: Work[] = [];
    for (const w of Object.values(this.data.db.works)) {
      if (!w?.t || w.t.length < 3) continue;
      if (seen.has(w.t)) continue;
      if (text.includes(w.t)) {
        seen.add(w.t);
        out.push(w);
      }
    }
    // エイリアスからもマッチ
    for (const [alias, canonical] of aliasToCanonical) {
      if (alias.length < 3) continue;
      if (text.includes(alias)) {
        if (seen.has(canonical)) continue;
        const w = Object.values(this.data.db.works).find((x) => x?.t === canonical);
        if (w) {
          seen.add(canonical);
          out.push(w);
        }
      }
    }
    return out;
  }

  private findInText(text: string): Map<string, number[]> {
    type M = { name: string; ids?: number[]; start: number; end: number; isWork: boolean };
    const matches: M[] = [];
    const N = text.length;

    for (let i = 0; i < N; i++) {
      const limit = Math.min(40, N - i);
      for (let len = 2; len <= limit; len++) {
        const sub = text.substring(i, i + len);
        if (!this.isAcceptableShort(sub)) continue;
        const ids = this.charNames.get(sub);
        if (!ids) continue;
        if (!isAtWordBoundary(text, i, i + len)) continue;
        matches.push({ name: sub, ids, start: i, end: i + len, isWork: false });
      }
    }
    for (const w of this.workNames) {
      if (w.length > N) continue;
      let idx = -1;
      while ((idx = text.indexOf(w, idx + 1)) >= 0) {
        matches.push({ name: w, start: idx, end: idx + w.length, isWork: true });
      }
    }

    matches.sort((a, b) => {
      const d = (b.end - b.start) - (a.end - a.start);
      if (d !== 0) return d;
      return (b.isWork ? 1 : 0) - (a.isWork ? 1 : 0);
    });
    const kept: M[] = [];
    for (const m of matches) {
      if (kept.some((k) => k.start <= m.start && m.end <= k.end)) continue;
      kept.push(m);
    }
    const out = new Map<string, number[]>();
    for (const k of kept) {
      if (k.isWork) continue;
      if (!out.has(k.name)) out.set(k.name, k.ids!);
    }
    return out;
  }
}
